import { GeoJSON, Geometry, Polygon, Feature } from "geojson";
import area from "@turf/area";

const filterPolygons = (geoJson: GeoJSON): Feature<Polygon> => {
  // Seems that TS can't handle .filter so we use "as" because
  // we definitely know that only polygons will be in array
  const polygons = flatten(geoJson).filter(
    (geom) => geom.type === "Polygon",
  ) as Polygon[];

  if (polygons.length === 0) {
    throw new Error("No polygons found");
  }

  // performance optimization to calculate all areas only once
  const polygonsAndAreas = polygons.map((polygon) => ({
    polygon,
    area: area(polygon),
  }));

  const sortedPolygonsAndAreas = polygonsAndAreas.sort((a, b) => {
    if (a.area === b.area) {
      return 0;
    }
    return a.area < b.area ? 1 : -1;
  });

  const polygonWithLargestArea = sortedPolygonsAndAreas[0].polygon;
  const polygonWithoutHoles = dropHoles(polygonWithLargestArea);

  return {
    type: "Feature",
    geometry: polygonWithoutHoles,
    properties: {},
  };
};

const flatten = (gj: GeoJSON): Geometry[] => {
  switch (gj.type) {
    case "FeatureCollection":
      return gj.features
        .map(flatten)
        .reduce((acc, curr) => acc.concat(curr), []);
    case "Feature":
      return gj.geometry ? flatten(gj.geometry) : [];
    case "MultiPoint":
      return gj.coordinates.map(function (_) {
        return { type: "Point", coordinates: _ };
      });
    case "MultiPolygon":
      return gj.coordinates.map(function (_) {
        return { type: "Polygon", coordinates: _ };
      });
    case "MultiLineString":
      return gj.coordinates.map(function (_) {
        return { type: "LineString", coordinates: _ };
      });
    case "Point":
    case "Polygon":
    case "LineString":
      return [gj];
    default:
      throw new Error(`Unsupported GeoJSON type ${gj.type}`);
  }
};

const dropHoles = (p: Polygon): Polygon => ({
  ...p,
  coordinates: [p.coordinates[0]],
});

export default filterPolygons;
